import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Fade,
  Avatar
} from '@mui/material';
import {
  Psychology,
  LocalHospital,
  Healing,
  Timer,
  CheckCircle,
  AccessTime,
  SelfImprovement,
  Spa,
  Mood,
  TrendingDown,
  Transform,
  EmojiEvents,
  AutoAwesome,
  Star
} from '@mui/icons-material';

const AnxietyLandingPage = () => {
  const navigate = useNavigate();
  const { session } = useAuth();

  // Redirect authenticated users to conversation
  useEffect(() => {
    if (session?.user) {
      navigate('/conversation');
    }
  }, [session, navigate]);

  const handleGetStarted = () => {
    navigate('/login?mode=signup');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const features = [
    {
      icon: <Transform sx={{ fontSize: '3rem', color: '#4CAF50' }} />,
      title: "Complete Anxiety Transformation",
      description: "Break free from panic attacks, overwhelming thoughts, and constant worry. Transform anxiety into calm confidence using proven therapeutic methods."
    },
    {
      icon: <SelfImprovement sx={{ fontSize: '3rem', color: '#2196F3' }} />,
      title: "Instant Relief Techniques",
      description: "Master 5-minute grounding exercises, breathing techniques, and mindfulness practices that stop anxiety attacks in their tracks."
    },
    {
      icon: <AutoAwesome sx={{ fontSize: '3rem', color: '#9C27B0' }} />,
      title: "Radiant Inner Peace",
      description: "Develop unshakeable calm, build emotional resilience, and experience the deep inner peace you've always deserved to feel."
    }
  ];

  const benefits = [
    "Stop anxiety attacks in under 5 minutes",
    "24/7 crisis support when you need it most",
    "Personalized grounding techniques that work",
    "Build long-term stress resilience",
    "Sleep better with reduced nighttime anxiety",
    "Regain control over racing thoughts"
  ];

  const testimonials = [
    {
      name: "Jessica M.",
      role: "Marketing Executive",
      result: "Eliminated panic attacks in 30 days",
      text: "I went from having 3-4 panic attacks daily to feeling completely calm and confident. The transformation techniques gave me my life back - I can finally breathe again.",
      avatar: "🌟"
    },
    {
      name: "Rachel S.",
      role: "College Student",
      result: "Overcame 10 years of social anxiety",
      text: "Social situations used to terrify me. Now I speak confidently in class and even gave a presentation last week. I feel like a completely different person - radiant and free.",
      avatar: "✨"
    },
    {
      name: "Amanda K.",
      role: "Working Mother",
      result: "From overwhelmed to peaceful in 6 weeks",
      text: "Juggling work and kids was causing constant overwhelm. These techniques taught me how to stay centered and peaceful no matter how chaotic life gets. Pure transformation.",
      avatar: "💫"
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)',
      position: 'relative'
    }}>
      {/* Calming Animated Background */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.15,
        background: `
          radial-gradient(circle at 25% 25%, #4CAF50 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, #2196F3 0%, transparent 50%),
          radial-gradient(circle at 50% 50%, #9C27B0 0%, transparent 70%),
          linear-gradient(45deg, transparent 40%, rgba(76, 175, 80, 0.1) 50%, transparent 60%)
        `,
        animation: 'backgroundPulse 8s ease-in-out infinite alternate'
      }} />

      {/* CSS Animation */}
      <style>
        {`
          @keyframes backgroundPulse {
            0% { opacity: 0.1; }
            100% { opacity: 0.2; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes glow {
            0%, 100% { box-shadow: 0 0 20px rgba(76, 175, 80, 0.3); }
            50% { box-shadow: 0 0 40px rgba(76, 175, 80, 0.6), 0 0 60px rgba(76, 175, 80, 0.4); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}
      </style>

      {/* Premium Header */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 10,
        py: { xs: 1, md: 1.5 },
        px: { xs: 2, md: 4 },
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #4CAF50, #2196F3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 25px rgba(76, 175, 80, 0.4)',
              animation: 'glow 3s ease-in-out infinite'
            }}>
              <img src="/sanity_logo.png" alt="Sanity Logo" style={{ 
                width: 36, 
                height: 36,
                filter: 'brightness(1.2)'
              }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                color: 'white',
                fontSize: { xs: '1.4rem', md: '1.8rem' },
                textShadow: '0 2px 10px rgba(0,0,0,0.5)'
              }}>
                Sanity Reps
              </Typography>
              <Typography variant="caption" sx={{ 
                color: '#4CAF50',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                letterSpacing: '1px'
              }}>
                ANXIETY TRANSFORMATION
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={handleLogin}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.3)',
                px: { xs: 2, md: 3 },
                py: 1,
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                fontWeight: 'bold',
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                '&:hover': { 
                  borderColor: '#4CAF50',
                  bgcolor: 'rgba(76, 175, 80, 0.1)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Login
            </Button>
            <Button 
              variant="contained" 
              onClick={handleGetStarted}
              sx={{ 
                background: 'linear-gradient(45deg, #4CAF50, #2196F3)',
                color: 'white',
                fontWeight: 'bold',
                px: { xs: 2.5, md: 4 },
                py: 1.2,
                fontSize: { xs: '0.85rem', md: '1rem' },
                borderRadius: 3,
                boxShadow: '0 8px 25px rgba(76, 175, 80, 0.4)',
                '&:hover': { 
                  background: 'linear-gradient(45deg, #45a049, #1976D2)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 35px rgba(76, 175, 80, 0.6)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              🌟 TRANSFORM NOW
            </Button>
          </Box>
        </Box>
      </Box>

      {/* HERO: Complete Anxiety Transformation */}
      <Container maxWidth="xl" sx={{ 
        position: 'relative', 
        zIndex: 10, 
        pt: { xs: 2, md: 2 },
        pb: { xs: 3, md: 2 },
        minHeight: { md: 'calc(100vh - 80px)' },
        maxHeight: { md: 'calc(100vh - 80px)' },
        display: { md: 'flex' },
        alignItems: { md: 'center' }
      }}>
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center" sx={{ height: '100%' }}>
          {/* LEFT: Compelling Copy */}
          <Grid item xs={12} md={8}>
            <Fade in timeout={800}>
              <Box sx={{ pr: { md: 3 } }}>
                {/* Attention-Grabbing Badge */}
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label="🌟 COMPLETE ANXIETY TRANSFORMATION SYSTEM" 
                    sx={{ 
                      background: 'linear-gradient(45deg, rgba(76, 175, 80, 0.2), rgba(33, 150, 243, 0.2))',
                      color: '#4CAF50',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', md: '0.85rem' },
                      border: '1px solid rgba(76, 175, 80, 0.5)',
                      backdropFilter: 'blur(10px)',
                      px: 2,
                      py: 0.5,
                      animation: 'float 3s ease-in-out infinite'
                    }} 
                  />
                </Box>

                {/* Power Headline */}
                <Typography variant="h1" sx={{ 
                  fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem', lg: '3.8rem' },
                  fontWeight: 900,
                  color: 'white',
                  mb: { xs: 2, md: 1.5 },
                  lineHeight: 0.95,
                  textShadow: '0 4px 20px rgba(0,0,0,0.7)',
                  letterSpacing: '-0.02em'
                }}>
                  TRANSFORM
                  <br />
                  <Box component="span" sx={{ 
                    background: 'linear-gradient(45deg, #4CAF50, #2196F3, #9C27B0)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    ANXIETY
                  </Box>
                  <br />
                  <Box component="span" sx={{ color: '#9C27B0' }}>
                    INTO RADIANCE
                  </Box>
                </Typography>
                
                {/* Power Subheadline */}
                <Typography variant="h4" sx={{ 
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' },
                  color: 'rgba(255,255,255,0.95)',
                  mb: { xs: 2.5, md: 3 },
                  fontWeight: 400,
                  lineHeight: 1.3,
                  maxWidth: '600px'
                }}>
                  Break free from panic attacks, overthinking, and constant worry. Become the <strong style={{color: '#4CAF50'}}>confident, radiant woman</strong> you're meant to be with proven anxiety transformation methods.
                </Typography>

                {/* Rapid Benefits */}
                <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                  <Grid container spacing={2}>
                    {[
                      { icon: '🌟', text: 'Stop panic attacks in 5 minutes', color: '#4CAF50' },
                      { icon: '✨', text: 'Transform worry into confidence', color: '#2196F3' },
                      { icon: '💫', text: 'Radiate calm inner peace', color: '#9C27B0' }
                    ].map((benefit, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5,
                          p: { xs: 1.5, md: 2 },
                          borderRadius: 3,
                          background: 'rgba(255,255,255,0.05)',
                          border: `1px solid rgba(${benefit.color === '#4CAF50' ? '76, 175, 80' : benefit.color === '#2196F3' ? '33, 150, 243' : '156, 39, 176'}, 0.3)`,
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            background: 'rgba(255,255,255,0.1)'
                          }
                        }}>
                          <Typography sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{benefit.icon}</Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'white', 
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', md: '0.9rem' }
                          }}>
                            {benefit.text}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                
                {/* CTA Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 2, md: 3 }, 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 3, md: 0 }
                }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={handleGetStarted}
                    sx={{ 
                      background: 'linear-gradient(45deg, #4CAF50, #2196F3)',
                      color: 'white',
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.5, md: 2 },
                      fontSize: { xs: '1rem', md: '1.3rem' },
                      fontWeight: 'bold',
                      borderRadius: 4,
                      boxShadow: '0 12px 35px rgba(76, 175, 80, 0.4)',
                      minWidth: { sm: '280px' },
                      '&:hover': { 
                        background: 'linear-gradient(45deg, #45a049, #1976D2)',
                        transform: 'translateY(-4px)',
                        boxShadow: '0 16px 45px rgba(76, 175, 80, 0.6)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🌟 TRANSFORM NOW
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => document.getElementById('testimonials').scrollIntoView({ behavior: 'smooth' })}
                    sx={{ 
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      px: 3,
                      py: { xs: 1.5, md: 2 },
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      borderRadius: 4,
                      backdropFilter: 'blur(10px)',
                      '&:hover': { 
                        bgcolor: 'rgba(255,255,255,0.1)',
                        borderColor: 'white',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    ✨ See Transformations
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Grid>
          
          {/* RIGHT: Powerful Anxiety Transformation Visual */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={1200}>
              <Box sx={{ 
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: { xs: 'auto', md: '420px' },
                mt: { xs: 2, md: 0 }
              }}>
                {/* Main Transformation Image */}
                <Box sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                  border: '3px solid rgba(76, 175, 80, 0.5)',
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                  backdropFilter: 'blur(20px)',
                  transition: 'all 0.4s ease',
                  maxWidth: { xs: '100%', md: '400px' },
                  width: '100%',
                  animation: 'float 6s ease-in-out infinite',
                  '&:hover': {
                    transform: 'scale(1.05) translateY(-10px)',
                    boxShadow: '0 35px 80px rgba(0,0,0,0.5)',
                    border: '3px solid rgba(76, 175, 80, 0.8)'
                  }
                }}>
                  <img 
                    src="/before_after_girl2.png" 
                    alt="Anxiety Transformation - From Worried to Radiant" 
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                  
                  {/* Premium Before/After Labels */}
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    background: 'linear-gradient(135deg, #dc3545, #c82333)',
                    color: 'white',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.8, md: 1 },
                    borderRadius: 4,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 6px 20px rgba(220, 53, 69, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    zIndex: 3,
                    animation: 'pulse 3s ease-in-out infinite',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 8px 25px rgba(220, 53, 69, 0.7)'
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    😰 Before: Anxious
                  </Box>
                  
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'linear-gradient(135deg, #28a745, #20c997)',
                    color: 'white',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.8, md: 1 },
                    borderRadius: 4,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 6px 20px rgba(40, 167, 69, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    zIndex: 3,
                    animation: 'glow 3s ease-in-out infinite',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 8px 25px rgba(40, 167, 69, 0.7)'
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    🌟 After: Radiant
                  </Box>
                  
                  {/* Results Overlay */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
                    color: 'white',
                    p: 3,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#4CAF50' }}>
                      🌟 ANXIETY TRANSFORMATION
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { number: "95%", label: "Less Worry", color: '#4CAF50' },
                        { number: "100%", label: "Confidence", color: '#2196F3' },
                        { number: "∞", label: "Peace", color: '#9C27B0' }
                      ].map((stat, index) => (
                        <Grid item xs={4} key={index}>
                          <Typography variant="h5" sx={{ 
                            color: stat.color, 
                            fontWeight: 'bold',
                            textShadow: `0 2px 10px ${stat.color}50`
                          }}>
                            {stat.number}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255,255,255,0.9)',
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}>
                            {stat.label}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>

                {/* Floating Achievement Indicators */}
                <Box sx={{
                  position: 'absolute',
                  top: -20,
                  left: -20,
                  background: 'linear-gradient(45deg, #4CAF50, #2196F3)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 20px rgba(76, 175, 80, 0.4)',
                  animation: 'float 4s ease-in-out infinite'
                }}>
                  ⭐ 5/5 Transformation
                </Box>
                
                <Box sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  background: 'linear-gradient(45deg, #2196F3, #9C27B0)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 20px rgba(33, 150, 243, 0.4)',
                  animation: 'float 4s ease-in-out infinite 2s'
                }}>
                  🌟 12,000+ Transformed
                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* FEATURES: Transformation Methods - Green/Blue Theme */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        position: 'relative',
        background: 'linear-gradient(135deg, #1b4332 0%, #2d5016 30%, #52b788 60%, #40916c 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 30% 30%, rgba(76, 175, 80, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 70% 70%, rgba(45, 80, 22, 0.4) 0%, transparent 50%)
          `,
          opacity: 0.8
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
              <Chip 
                label="🌟 PROVEN TRANSFORMATION METHODS" 
                sx={{ 
                  mb: 3, 
                  background: 'linear-gradient(45deg, rgba(76, 175, 80, 0.3), rgba(45, 80, 22, 0.3))',
                  color: '#66BB6A',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                  border: '1px solid rgba(76, 175, 80, 0.5)',
                  backdropFilter: 'blur(10px)',
                  px: 3,
                  py: 1
                }} 
              />
              <Typography variant="h2" sx={{ 
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                color: 'white',
                mb: 3,
                textShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }}>
                Transform Anxiety Into
                <Box component="span" sx={{ 
                  background: 'linear-gradient(45deg, #66BB6A, #4CAF50)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: 'block'
                }}>
                  Radiant Confidence
                </Box>
              </Typography>
              <Typography variant="h5" sx={{ 
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 400,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.4
              }}>
                Breakthrough techniques to eliminate panic attacks, calm racing thoughts, and develop unshakeable inner peace
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in timeout={1200 + index * 200}>
                  <Card sx={{ 
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 4,
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      background: 'linear-gradient(145deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%)',
                      boxShadow: '0 20px 50px rgba(76, 175, 80, 0.3)'
                    }
                  }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Box sx={{
                        display: 'inline-flex',
                        p: 2,
                        borderRadius: '50%',
                        background: 'linear-gradient(45deg, rgba(76, 175, 80, 0.2), rgba(45, 80, 22, 0.2))',
                        border: '2px solid rgba(76, 175, 80, 0.4)',
                        mb: 2
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 'bold', 
                        color: 'white',
                        mb: 2,
                        textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                      }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: 'rgba(255,255,255,0.9)',
                        lineHeight: 1.6,
                        fontSize: '1.1rem'
                      }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TESTIMONIALS: Success Stories - Blue/Purple Theme */}
      <Box id="testimonials" sx={{ 
        py: { xs: 8, md: 12 },
        position: 'relative',
        background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 30%, #42A5F5 60%, #64B5F6 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 25% 25%, rgba(33, 150, 243, 0.4) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(25, 118, 210, 0.3) 0%, transparent 50%)
          `,
          opacity: 0.8
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
              <Chip 
                label="✨ REAL ANXIETY TRANSFORMATIONS" 
                sx={{ 
                  mb: 3, 
                  background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.3), rgba(25, 118, 210, 0.3))',
                  color: '#42A5F5',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                  border: '1px solid rgba(33, 150, 243, 0.5)',
                  backdropFilter: 'blur(10px)',
                  px: 3,
                  py: 1
                }} 
              />
              <Typography variant="h2" sx={{ 
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                color: 'white',
                mb: 3,
                textShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }}>
                Inspiring Stories of
                <Box component="span" sx={{ 
                  background: 'linear-gradient(45deg, #42A5F5, #2196F3)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: 'block'
                }}>
                  Complete Transformation
                </Box>
              </Typography>
              <Typography variant="h5" sx={{ 
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 400,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.4
              }}>
                See how women just like you overcame anxiety and stepped into their radiant, confident selves
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in timeout={1200 + index * 200}>
                  <Card sx={{ 
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 4,
                    p: 4,
                    height: '100%',
                    position: 'relative',
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      background: 'linear-gradient(145deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%)',
                      boxShadow: '0 20px 50px rgba(33, 150, 243, 0.3)'
                    }
                  }}>
                    {/* Success Badge */}
                    <Box sx={{
                      position: 'absolute',
                      top: -10,
                      right: 20,
                      background: 'linear-gradient(45deg, #4CAF50, #66BB6A)',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 3,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)'
                    }}>
                      ✅ {testimonial.result}
                    </Box>

                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                        <Avatar sx={{ 
                          bgcolor: 'linear-gradient(45deg, #42A5F5, #2196F3)', 
                          mr: 2,
                          width: 56,
                          height: 56,
                          fontSize: '1.5rem'
                        }}>
                          {testimonial.avatar}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            color: 'white',
                            textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                          }}>
                            {testimonial.name}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ 
                            color: '#42A5F5',
                            fontWeight: 600
                          }}>
                            {testimonial.role}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography variant="body1" sx={{ 
                        color: 'rgba(255,255,255,0.9)',
                        lineHeight: 1.6,
                        fontSize: '1rem',
                        fontStyle: 'italic',
                        mb: 3
                      }}>
                        "{testimonial.text}"
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {[1,2,3,4,5].map((star) => (
                          <Star key={star} sx={{ color: '#FFD700', fontSize: '1.5rem' }} />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FINAL CTA: Begin Your Transformation */}
      <Container maxWidth="md" sx={{ 
        position: 'relative', 
        zIndex: 10, 
        py: { xs: 8, md: 12 },
        textAlign: 'center'
      }}>
        <Paper sx={{
          background: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          backdropFilter: 'blur(30px)',
          border: '2px solid rgba(76, 175, 80, 0.3)',
          borderRadius: 6,
          p: { xs: 4, md: 6 },
          textAlign: 'center',
          boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
          animation: 'glow 4s ease-in-out infinite'
        }}>
          <Typography variant="h3" sx={{ 
            fontSize: { xs: '2rem', md: '2.8rem' },
            fontWeight: 'bold',
            color: 'white',
            mb: 2,
            textShadow: '0 4px 20px rgba(0,0,0,0.5)'
          }}>
            Ready for Your Transformation?
          </Typography>
          
          <Typography variant="h5" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            color: 'rgba(255,255,255,0.9)',
            mb: 4,
            maxWidth: '500px',
            margin: '0 auto 2rem auto',
            lineHeight: 1.4
          }}>
            Join thousands of women who've transformed anxiety into radiant confidence and unshakeable inner peace
          </Typography>

          <Button 
            variant="contained" 
            size="large"
            onClick={handleGetStarted}
            sx={{ 
              background: 'linear-gradient(45deg, #4CAF50, #2196F3)',
              color: 'white',
              px: { xs: 4, md: 6 },
              py: { xs: 2, md: 2.5 },
              fontSize: { xs: '1.1rem', md: '1.4rem' },
              fontWeight: 'bold',
              borderRadius: 5,
              boxShadow: '0 15px 40px rgba(76, 175, 80, 0.4)',
              '&:hover': { 
                background: 'linear-gradient(45deg, #45a049, #1976D2)',
                transform: 'translateY(-5px)',
                boxShadow: '0 20px 50px rgba(76, 175, 80, 0.6)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            🌟 Begin My Transformation
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default AnxietyLandingPage;
